if (!customElements.get("tax-form-validator")) {
  class TaxFormValidator extends HTMLElement {
    constructor() {
      super();
      this.isFetching = false;
      this.canSubmit = false;
      this.isLoading = false;
      this.endpoint = null;
      this.taxInputs = [];
      this.cartForm = null;
      this.taxFormActions = this.querySelector(".tax-form__actions");
      if (this.taxFormActions) {
        this.taxFormActions.querySelectorAll("a").forEach((action) => {
          action.addEventListener("click", this.handleTaxFormActions.bind(this));
        });
      }
      this.validateVatButton = this.querySelector(".validate-vat");
      this.validationMessage = this.querySelector(".vat-validation-message");
      if (this.validateVatButton) {
        this.validateVatButton.addEventListener("click", this.handleValidateVat.bind(this));
      }
    }

    set isLoading(isLoading) {
      this._isLoading = !!isLoading;
      this.classList.remove("loading");
      if (isLoading) {
        this.classList.add("loading");
      }
    }

    get isLoading() {
      return this._isLoading;
    }

    highlightEmptyInputs() {
      this.taxInputs.forEach((input) => {
        this.toggleInputError({ input });
        if (input.value.trim().length === 0) {
          this.toggleInputError({ input, message: translations["required-input"] || "Field required" });
        }
      });
    }

    validateHungarianVAT(vat) {
      const cleanVAT = vat.replace(/[\s-]/g, "").toUpperCase();
      const hungarianVATPattern = /^HU\d+$/;
      return hungarianVATPattern.test(cleanVAT);
    }

    generatePayload() {
      const payload = {
        company_id: "",
        company_name: "",
        email: "",
        myshopify_domain: Shopify.shop,
        vat: "",
      };

      this.taxInputs.forEach((input) => {
        const name = input.name;
        if (name === "attributes[ico]") {
          payload.company_id = input.value;
        } else if (name === "attributes[firma]") {
          payload.company_name = input.value;
        } else if (name === "attributes[email]") {
          payload.email = input.value;
        } else if (name === "attributes[dic]") {
          payload.vat = input.value;
        }
      });

      return payload;
    }

    async sendRequest(method) {
      const payload = this.generatePayload();
      this.isFetching = true;

      try {
        const request = await fetch(this.endpoint, {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": Shopify.locale,
          },
          method: method,
          body: JSON.stringify(payload),
        });

        let data = {};
        try {
          const contentType = request.headers.get("content-type") || "";
          const text = await request.text();
          if (text && contentType.includes("application/json")) {
            data = JSON.parse(text);
          }
        } catch (parseErr) {
          console.warn("Response parse skipped:", parseErr);
        }
        this.isFetching = false;
        return { data };
      } catch (error) {
        this.isFetching = false;
        return { data: { errors: { general: "Validation failed. Please try again." } } };
      }
    }

    toggleInputError({ input, message = "" }) {
      if (!input) return;

      const wrapper = input.closest(".field__wrapper");
      if (!wrapper) return;

      const messageContainer = wrapper.querySelector(".field__message");
      if (!messageContainer) return;

      wrapper.classList.remove("field--error");
      if (message.length > 0) {
        wrapper.classList.add("field--error");
      }
      messageContainer.innerText = message;
    }

    setValidationMessage(message, isError = false) {
      if (!this.validationMessage) return;
      this.validationMessage.innerText = message || "";
      this.validationMessage.classList.remove("error", "success");
      if (message) {
        this.validationMessage.classList.add(isError ? "error" : "success");
        this.validationMessage.style.display = "block";
      } else {
        this.validationMessage.style.display = "none";
      }
    }

    toggleFormActions(show) {
      if (!this.taxFormActions) return;
      this.taxFormActions.classList.toggle("hidden", !show);
      if (show) {
        this.taxInputs.forEach((input) => {
          input.disabled = true;
        });
      }
    }

    displayErrors(errors) {
      const errorKeysWithInputNames = {
        email: "attributes[email]",
        company_name: "attributes[firma]",
        company_id: "attributes[ico]",
        vat: "attributes[dic]",
      };

      for (const key in errors) {
        const inputError = errors[key];
        const message = typeof inputError === "string" ? inputError : inputError.join(" ");
        const input = this.taxInputs.find((input) => input.name === errorKeysWithInputNames[key]);
        this.toggleInputError({ input, message });
      }
    }

    cloneTaxInputsToCartForm() {
      if (!this.cartForm) return;

      this.taxInputs.forEach((input) => {
        const inputInCartForm = this.cartForm.querySelector(`input[name="${input.name}"]`);

        if (inputInCartForm) {
          inputInCartForm.value = input.value;
        } else {
          const inputClone = input.cloneNode();
          inputClone.value = input.value;
          inputClone.type = "hidden";
          this.cartForm.appendChild(inputClone);
        }
      });

      const dicInput = this.taxInputs.find((input) => input.name === "attributes[dic]");
      if (dicInput && dicInput.value.trim().length > 0) {
        const vatPayerInput = this.cartForm.querySelector('input[name="attributes[VATPayer]"]');
        if (vatPayerInput) {
          vatPayerInput.value = "true";
        } else {
          const vatPayerInputClone = document.createElement("input");
          vatPayerInputClone.name = "attributes[VATPayer]";
          vatPayerInputClone.value = "true";
          vatPayerInputClone.type = "hidden";
          this.cartForm.appendChild(vatPayerInputClone);
        }
      }
    }

    getInputsWithValueCount() {
      return this.taxInputs.filter((input) => {
        return input.name !== "attributes[email]" && input.value.trim().length > 0;
      }).length;
    }

    getTaxInputsWithoutEmail() {
      return this.taxInputs.filter((input) => input.name !== "attributes[email]");
    }

    async updateCartAttribute(input) {
      if (!input || !input.name) return;

      const taxInputsWithValueCount = this.getInputsWithValueCount();

      if (taxInputsWithValueCount === 0) {
        await this.clearCartVatAttributes();
      } else {
        const formData = new FormData();
        formData.append(input.name, input.value);

        if (input.name === "attributes[dic]" && input.value.trim().length === 0) {
          formData.append("attributes[VATPayer]", "");
        }

        try {
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams(formData),
          });
        } catch (error) {
          console.error("Failed to update cart attribute:", error);
        }
      }
    }

    async persistCartAttributes() {
      const attributes = {};
      this.taxInputs.forEach((input) => {
        if (input.name && input.name.includes("attributes[")) {
          const attrName = input.name.match(/attributes\[(.*?)\]/)[1];
          attributes[attrName] = input.value;
        }
      });

      const dicInput = this.taxInputs.find((input) => input.name === "attributes[dic]");
      attributes["VATPayer"] = dicInput && dicInput.value.trim().length > 0 ? "true" : "";

      try {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ attributes }),
        });
      } catch (error) {
        console.error("Failed to update cart:", error);
      }
    }

    async clearCartVatAttributes() {
      const attributes = { ico: "", firma: "", email: "", dic: "", VATPayer: "" };
      try {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ attributes }),
        });
      } catch (error) {
        console.error("Failed to clear cart VAT attributes:", error);
      }
    }

    async handleValidateVat(e) {
      e.preventDefault();
      if (this.isFetching) return;

      this.setValidationMessage("");
      this.taxInputs.forEach((input) => this.toggleInputError({ input }));

      const taxInputsWithValueCount = this.getInputsWithValueCount();
      const taxInputsWithoutEmail = this.getTaxInputsWithoutEmail();

      if (taxInputsWithValueCount === 0 || taxInputsWithValueCount !== taxInputsWithoutEmail.length) {
        this.highlightEmptyInputs();
        this.setValidationMessage(translations["required-input"] || "Please fill all required fields", true);
        return;
      }

      const vatInput = this.taxInputs.find((input) => input.name === "attributes[dic]");
      if (vatInput && vatInput.value.trim().length > 0 && !this.validateHungarianVAT(vatInput.value)) {
        const message = translations["invalid-vat"] || "Only Hungarian VAT numbers (HU followed by digits) are accepted";
        this.toggleInputError({ input: vatInput, message });
        this.setValidationMessage(message, true);
        return;
      }

      this.isLoading = true;
      const { data } = await this.sendRequest("POST");
      this.isLoading = false;

      if (data.errors && Object.keys(data.errors).length > 0) {
        this.displayErrors(data.errors);
        this.setValidationMessage(translations["error"] || "Validation failed. Please review the highlighted fields.", true);
        return;
      }

      this.cloneTaxInputsToCartForm();
      await this.persistCartAttributes();
      this.canSubmit = true;
      this.setValidationMessage(translations["success"] || "VAT validated and saved.", false);
      this.toggleFormActions(true);
    }

    async handleTaxFormActions(e) {
      e.preventDefault();
      this.setValidationMessage("");
      this.isLoading = true;
      this.taxInputs.forEach((input) => {
        // remove everything except email as email is needed for DELETE method
        if (input.name !== "attributes[email]") {
          input.value = "";
        }
      });
      const { data } = await this.sendRequest("DELETE");
      if (data.errors && Object.keys(data.errors).length > 0) {
        this.displayErrors(data.errors);
      } else {
        const emailInput = this.taxInputs.find((input) => input.name === "attributes[email]");
        if (emailInput) {
          emailInput.value = "";
        }
        await this.clearCartVatAttributes();
        this.resetInputs();
      }
      this.cloneTaxInputsToCartForm();
      this.isLoading = false;
      this.canSubmit = true;
    }

    resetInputs() {
      this.taxInputs.forEach((input) => {
        input.value = "";
        input.removeAttribute("disabled");
      });
    }

    connectedCallback() {
      this.endpoint = this.getAttribute("data-endpoint");
      if (!this.endpoint) return;

      const cartFormSelector = this.getAttribute("data-cart-form-selector");

      if (cartFormSelector) {
        this.cartForm = document.querySelector(cartFormSelector);
      }

      if (!this.cartForm) {
        const parentContainer = this.closest('[id*="cart"], [class*="cart"]');
        if (parentContainer) {
          this.cartForm = parentContainer.querySelector('form[action*="/cart"]') || parentContainer.querySelector("form");
        }

        if (!this.cartForm) {
          this.cartForm = this.closest("form") || document.querySelector('form[action="/cart"]');
        }
      }

      if (!this.cartForm) return;

      this.taxInputs = Array.from(this.querySelectorAll(".tax-input"));

      this.taxInputs.forEach((input) => {
        input.addEventListener("input", () => {
          this.toggleInputError({ input });
          this.setValidationMessage("");
        });

        input.addEventListener("blur", () => {
          this.updateCartAttribute(input);
          this.setValidationMessage("");
        });
      });
    }
  }

  customElements.define("tax-form-validator", TaxFormValidator);
}
